import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Button, Divider, Drawer, Spin } from "antd";
import { FormProvider, useForm } from "react-hook-form";
import {
    createSurveyValidationSchema,
    generateDefaultPage,
} from "../../../utils/surveys";
import { yupResolver } from "@hookform/resolvers/yup";
import {
    useCreateSurveyMutation,
    useGetEnvironmentQuery,
    useGetSurveyByIdQuery,
    useUpdateSurveyByIdMutation,
} from "../../../store/api/formbricks/survey";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import QuestionsTab from "./QuestionsTab";
import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import { downloadObjectAsJson } from "../../../utils/appUtils";

const DrawerHeader = ({ setFormValues, title, survey }) => {
    const ref = useRef();

    const uploadHandler = () => {
        ref.current.click();
    };

    const exportHandler = () => {
        if (survey) downloadObjectAsJson(survey, survey.name);
    };

    const changeFileInputHandler = (event) => {
        const files = event.target.files;
        const reader = new FileReader();

        reader.onload = function (e) {
            const result = JSON.parse(e.target.result);
            if (result.id) {
                delete result.id;
            }
            setFormValues(result);
        };

        reader.readAsText(files[files.length - 1]);
    };

    return (
        <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
            <p>{title}</p>
            {title === "Create survey" ? (
                <>
                    <Button onClick={uploadHandler} icon={<UploadOutlined />}>
                        Import
                    </Button>
                    <input
                        type="file"
                        ref={ref}
                        style={{ display: "none" }}
                        onChange={changeFileInputHandler}
                    />
                </>
            ) : (
                <>
                    <Button onClick={exportHandler} icon={<DownloadOutlined />}>
                        Export as JSON
                    </Button>
                </>
            )}
        </div>
    );
};

export const CreateEditSurveyModal = ({
    surveyTemplate,
    isOpen,
    onClose,
    surveyId = "",
}) => {
    const screens = useBreakpoint();
    const { data: fetchedSurvey } = useGetSurveyByIdQuery(surveyId, {
        skip: !surveyId,
    });

    const survey = useMemo(
        () => (surveyId ? fetchedSurvey : surveyTemplate ? surveyTemplate : {}),
        [fetchedSurvey, surveyId, surveyTemplate]
    );
    const { data: formbricksEnvironment, isFetching: isEnvironmentLoading } =
        useGetEnvironmentQuery();

    const [createSurvey, { isLoading: createSurveyLoading }] =
        useCreateSurveyMutation();
    const [updateSurvey, { isLoading: updateSurveyLoading }] =
        useUpdateSurveyByIdMutation();

    const isLoading =
        createSurveyLoading || updateSurveyLoading || isEnvironmentLoading;

    const defaultValues = useMemo(
        () => ({
            name: survey?.name,
            image: survey?.image,
            description: survey?.description,
            status: survey?.status || undefined,
            pages: survey?.pages || [generateDefaultPage()],
            questions: survey?.questions || [],
            thankYouCard: {
                enabled: true,
                headline: "Thank you!",
                subheader: "We appreciate your feedback.",
            },
            productOverwrites: {
                brandColor: survey?.productOverwrites?.brandColor || "#0396C2",
            },
            styling: {
                background: {
                    bg: survey?.styling?.background?.bg || "#0396c214",
                },
            },
            isPublic: survey?.isPublic || false,
            createProject: false,
        }),
        [
            survey?.image,
            survey?.name,
            survey?.status,
            survey?.questions?.length,
            survey?.description,
            survey?.pages,
            survey?.productOverwrites?.brandColor,
            survey?.styling?.background?.bg,
            survey?.isPublic,
        ]
    );

    const [collapsedPage, setCollapsedPage] = useState(null);

    const methods = useForm({
        defaultValues,
        resolver: yupResolver(createSurveyValidationSchema),
    });

    const { reset, handleSubmit, watch } = methods;

    const submitFormHandler = async (data) => {
        const createBody = {
            ...data,
            type: "link",
            thankYouCard: {
                enabled: true,
                headline: "Thank you!",
                subheader: "We appreciate your feedback.",
            },
            projects: [],
        };

        const updateBody = {
            ...data,
        };

        if (surveyId) {
            await updateSurvey({ id: surveyId, body: updateBody }).unwrap();
        } else {
            await createSurvey(createBody).unwrap();
        }
        reset(defaultValues);
        onClose();
    };

    const actualSurvey = watch();

    const iframeRef = useRef(null);
    const [isIframeReady, setIframeReady] = useState(false);

    useEffect(() => {
        const handleIframeMessage = (event) => {
            if (event.data.status === "ready") {
                setIframeReady(true);
            }
        };

        window.addEventListener("message", handleIframeMessage);

        return () => {
            window.removeEventListener("message", handleIframeMessage);
        };
    }, []);

    const sendMessageToFrame = useCallback(() => {
        const frame = document.getElementById("previewFrame");
        if (frame && isIframeReady) {
            const message = {
                survey: actualSurvey,
                environmentId: formbricksEnvironment?.id,
                selectedPage: collapsedPage,
            };
            frame.contentWindow.postMessage(
                message,
                `${process.env.REACT_APP_FORMBRICKS_URL}preview`
            );
        }
    }, [actualSurvey, formbricksEnvironment, isIframeReady, collapsedPage]);

    useEffect(() => {
        reset(defaultValues);
    }, [defaultValues, reset]);

    useEffect(() => {
        if (formbricksEnvironment)
            if (surveyId) {
                if (survey) {
                    sendMessageToFrame();
                }
            } else {
                sendMessageToFrame();
            }
    }, [formbricksEnvironment, sendMessageToFrame, survey, surveyId]);

    return (
        <Drawer
            width={"100%"}
            title={
                surveyId ? (
                    <DrawerHeader title="Edit survey" survey={survey} />
                ) : (
                    <DrawerHeader setFormValues={reset} title="Create survey" />
                )
            }
            style={{
                maxHeight: "100%",
                paddingBottom: 0,
                overflow: screens.lg ? "unset" : "auto",
            }}
            bodyStyle={{
                display: "flex",
                flexDirection: screens.lg ? "row" : "column",
                overflow: "unset",
            }}
            headerStyle={{ color: "#fff" }}
            open={isOpen}
            onClose={onClose}
            placement="left"
            okButtonProps={{
                form: "create-survey",
                htmlType: "submit",
                loading: isLoading,
            }}
        >
            <FormProvider {...methods}>
                <form
                    id="create-survey"
                    onSubmit={handleSubmit(submitFormHandler)}
                    style={{
                        width: "100%",
                    }}
                >
                    <QuestionsTab
                        collapsedPage={collapsedPage}
                        setCollapsedPage={setCollapsedPage}
                    />
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            padding: "20px",
                            boxSizing: "border-box",
                            borderTop: "1px solid rgba(5, 5, 5, 0.06)",
                        }}
                    >
                        <Button
                            size="middle"
                            loading={isLoading}
                            htmlType="submit"
                            className="project-list button get-data"
                            style={{
                                fontWeight: 700,
                            }}
                        >
                            Submit
                        </Button>
                    </div>
                </form>
            </FormProvider>
            <Divider
                style={{ height: "100%", margin: "10px 0 0 0" }}
                type={screens.lg ? "vertical" : "horizontal"}
            />
            <div
                style={{
                    width: "100%",
                    height: "100%",
                    padding: "20px",
                    boxSizing: "border-box",
                }}
            >
                {!isIframeReady && (
                    <div
                        style={{
                            display: "flex",
                            width: "100%",
                            height: "100%",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        <Spin></Spin>
                    </div>
                )}
                <iframe
                    ref={iframeRef}
                    title="Survey preview"
                    style={{
                        width: "100%",
                        height: "100%",
                        minHeight: "800px",
                        display: !isIframeReady ? "none" : "block",
                        border: "none",
                        borderRadius: "0.5rem",
                    }}
                    id="previewFrame"
                    src={`${process.env.REACT_APP_FORMBRICKS_URL}preview`}
                ></iframe>
            </div>
        </Drawer>
    );
};
